import { useCallback, useEffect, useMemo, useState } from "react";
import type { Device, DeviceListing, GoonSession, View } from "@gooner/shared";
import { PAIRING_CODE_LEN, templateForViewId, viewIdKind } from "@gooner/shared";
import { api } from "../../lib/api";
import { track } from "../../analytics";
import { useAnalyticsRole } from "../../lib/useAnalyticsRole";
import { useSession } from "../../lib/session";
import { AuthModal } from "../components/AuthModal";
import { BottomSheet, SheetBtn, WhatsPlayingSheet } from "../../components/WhatsPlaying";
import { useDevicePresence } from "../hooks/useDevicePresence";
import { usePairingCode } from "../hooks/usePairingCode";
import { hostLabel } from "../../lib/hostLabel";
import { useServerClockOffset } from "../../lib/serverClock";
import { liveDevices, stepLostScreen, lostScreenView, NO_LOST_SCREEN, type LostScreenState } from "../lib/rail";
import { assignView } from "../lib/assign";
import { deviceEmoji } from "../lib/deviceEmoji";
import { SiteChrome } from "../../components/SiteChrome";
import { IconClockRecord } from "../ui/icons";
import { cx } from "../ui/primitives";
import { DiscordLink } from "../../components/DiscordLink";
import { SessionSummaryModal } from "../../display/SessionSummaryModal";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { formatElapsed } from "../../lib/elapsed";
import { holdCountdown, useCountdown } from "../../lib/useCountdown";
import { Masked, maskIf } from "../../lib/replayMask";

type Sheet = "screen" | "view" | null;

/** How often the open remote re-reads /devices — the clock that keeps each row's screen
 *  code current (codes are joined into the listing, not pushed over the socket). Matches
 *  the screens' own announce cadence, so faster would learn nothing sooner. */
const DEVICES_REFRESH_MS = 10_000;

/**
 * How long a screen you JUST connected is shown regardless of presence.
 *
 * `liveDevices` filters on presence, and presence only exists once that screen's socket
 * has joined under its new device id — which is a second or three away, because the screen
 * has to poll, learn its device, navigate and reconnect. For that gap the rail said
 * "nothing here" about a screen you had this moment paired, which reads as a failed claim;
 * the reasonable response to it is to type the code again.
 *
 * Presence is still the rule for every OTHER screen. This is narrower than it looks: it
 * covers one device, from an act taken on this remote, for as long as the pairing needs.
 */
const JUST_CLAIMED_MS = 15_000;

/** How often a SELECTED screen is re-pulsed with identify. The screen holds its overlay
 *  for IDENTIFY_MS (25s) per pulse, so this keeps it continuously lit while the row is
 *  selected — and a remote that dies mid-selection strands the light for at most one
 *  hold window instead of forever. */
const IDENTIFY_PULSE_MS = 10_000;

/** What a screen is showing, as the remote prints it. `user` says the name is somebody's
 *  words (a saved view, a collection the picker minted a view from, a room) and is masked
 *  in session replay; built-ins and our own "Nothing" / "Something" stay readable. */
export type Showing = { name: string; icon: string; user: boolean };

/** True for a view id whose name a PERSON chose: their saved views, the temp views minted
 *  from a collection (named after it), and rooms. Built-ins, walls and Infinity are ours. */
export const isUserNamedView = (id: string): boolean => {
  const kind = viewIdKind(id);
  return kind === "saved" || kind === "temp" || kind === "room";
};

/**
 * The one resolver for "what is this screen playing" — the rail prints icon and name, the
 * big button the name alone, and both come from here or the copies drift.
 *
 * Built-ins are resolved first (via the shared templateForViewId): they belong to nobody,
 * so they never appear in `views` and would otherwise fall through to "Something" — the
 * answer for a view that really has gone (deleted while playing), and the two must not
 * look alike. `minted` is the remote's own temp views (a tapped collection), which appear
 * in neither of the other lists.
 *
 * Exported, with ShowingName below, so tools/checks/replay-mask.mts can push a sentinel
 * through the same variable path the screen uses: the name travels through this object,
 * which is exactly what the static ratchet cannot follow.
 */
export function resolveShowing(
  id: string | null,
  views: readonly Pick<View, "id" | "name" | "icon">[],
  minted: Readonly<Record<string, { name: string; icon: string }>>,
): Showing {
  if (!id) return { name: "Nothing", icon: "—", user: false };
  const t = templateForViewId(id);
  if (t) return { name: t.name, icon: t.emoji, user: false };
  const v = views.find((x) => x.id === id);
  if (v) return { name: v.name, icon: v.icon || "🖥️", user: true };
  const m = minted[id];
  if (m) return { name: m.name, icon: m.icon, user: true };
  return { name: "Something", icon: "🎬", user: false };
}

/** A resolved "Now showing" name — masked in replay when a person wrote it. */
export function ShowingName({ showing, className }: { showing: Showing; className?: string }) {
  return (
    <span className={className} {...maskIf(showing.user)}>
      {showing.name}
    </span>
  );
}

/** A rail row's identity: the screen's code, or — for a tab announcing none — its name.
 *  Both are masked in replay: the name is the owner's words, and the code is a live
 *  pairing code (whoever types it takes the screen), masked like the Rooms invite link. */
export function DeviceIdentity({ device }: { device: Pick<DeviceListing, "code" | "name"> }) {
  return (
    <span
      className={cx(
        "text-sm font-semibold text-zinc-100",
        device.code ? "font-mono tracking-[0.2em]" : "",
      )}
    >
      <Masked>{device.code ?? device.name}</Masked>
    </span>
  );
}

/**
 * The Goontroller — a phone-first, single-screen remote for the wall.
 *
 * It predates the views/devices model and was deleted with the caves; this is it rebuilt
 * against what the app is now. The grammar is the part worth keeping and is the reason it
 * exists at all: **big targets, bottom sheets, and the session controls right on the
 * face.** A phone in one hand is not a desktop page at a narrower width — the home screen
 * reordered for mobile is a library you scroll, and this is a remote you press.
 *
 * It is now a named destination rather than somewhere you could only be sent: "Remote" on
 * the front door comes straight here. That is what forced `ConnectScreen` below — a role
 * you can CHOOSE has to work for someone arriving with nothing, and this page used to open
 * on an empty device list and an instruction to go scan something elsewhere.
 *
 * Everything here drives LIVE screens: it connects one, points it at a view, and steers
 * what is playing. It creates nothing and saves nothing — building views is the editor's
 * job, and a remote that could also edit would need a keyboard.
 */
export function Goontroller() {
  const [session, setSession] = useState<GoonSession | null>(null);
  /* The finished session, held open as the recap card.
     Both stop paths used to be `void api.session.stop().catch(...)` — the summary the
     server had just built went straight on the floor, so ending a session from the phone
     showed a toast and nothing else. The phone is the device with the share sheet on it;
     it is the LAST surface that should have been silent. */
  const [summary, setSummary] = useState<{ session: GoonSession } | null>(null);
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  // The session arrives live now, over the same control socket presence rides. Without it
  // this page fetched the session once and then only learned about the edges IT added:
  // the timer ran, but the view count sat still while the wall's climbed.
  const { presence, ready, connected, sendCommand } = useDevicePresence(setSession);
  const [devices, setDevices] = useState<DeviceListing[]>([]);
  const [views, setViews] = useState<View[]>([]);
  // Temp views the picker minted (a tapped collection), by view id. They appear in no
  // list — `views` holds saved rows, templates are built-ins — so without this the rail
  // would call a freshly-pointed collection "Something".
  const [minted, setMinted] = useState<Record<string, { name: string; icon: string }>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  // Which screen is LIT, separately from which one is aimed. They start together (picking
  // a row flashes it), but a completed change lowers the light while keeping the aim —
  // see point() and the identify effect.
  const [litId, setLitId] = useState<string | null>(null);
  // Screens claimed from this remote and the instant it happened, so the rail can show one
  // before its socket has landed. See JUST_CLAIMED_MS.
  const [justClaimed, setJustClaimed] = useState<Record<string, number>>({});
  const [now, setNow] = useState(() => Date.now());
  /* The session's clock is the SERVER's. Subtracting `startedAt` — a server instant —
     from this phone's `Date.now()` measured how far this phone's clock is from the
     server's, which is why a desktop with a slow clock showed every one of its tabs a
     minute behind the phone in the same room. Resampled every so often; the tick below
     still counts locally between samples. */
  const clockOffset = useServerClockOffset();
  const [sheet, setSheet] = useState<Sheet>(null);
  // `masked`: the flash repeats a name a person chose (point()'s label), so it is masked
  // in replay like the list it was picked from. Every other toast is our own copy.
  const [flash, setFlash] = useState<{ msg: string; masked: boolean } | null>(null);
  const user = useSession((s) => s.user);
  const [auth, setAuth] = useState<null | "login" | "register">(null);
  // Dismissal is per-mount, not persisted. Nothing here is worth a storage key, and a
  // remote is a tab you open for a session rather than a page you live on.
  const [hideSignIn, setHideSignIn] = useState(false);

  useAnalyticsRole("remote");
  // The phone-first remote being opened at all. Once per mount, not per render: the
  // question is "does anyone use the remote", and the empty dep array is what keeps this
  // one event rather than one per state change on a screen that updates constantly.
  useEffect(() => {
    track("goontroller_connected");
  }, []);

  const toast = useCallback((msg: string, masked = false) => {
    const entry = { msg, masked };
    setFlash(entry);
    window.setTimeout(() => setFlash((f) => (f === entry ? null : f)), 1200);
  }, []);

  const loadDevices = useCallback(async () => {
    try {
      setDevices((await api.devices.list()).devices);
    } catch {
      // Keep what we had. This runs on a 10s clock now, and one dropped request must
      // not empty the rail — which would also stand the selected screen's light down
      // mid-selection. Presence, not this list, is what decides live-ness.
    }
  }, []);

  useEffect(() => {
    void loadDevices();
    // Views are fetched here for the RAIL's benefit (naming what each row shows), not
    // the picker's — the shared sheet loads its own on open.
    void api.views.list().then((r) => setViews(r.views)).catch(() => setViews([]));
    void api.session.get().then((r) => setSession(r.session)).catch(() => undefined);
  }, [loadDevices]);

  /*
   * Keep the listing fresh while the remote is open — this is what keeps the CODE in
   * each row honest. A screen announces its code only after it exists (after the claim,
   * after its tab loads), so the fetch that followed the claim reliably lands too early
   * and prints the name fallback forever. Presence can't carry the fix: it pushes socket
   * state, not registry state. Same clock as the screens' own renewals, so a code is
   * never more than one beat stale.
   */
  useEffect(() => {
    const t = setInterval(() => void loadDevices(), DEVICES_REFRESH_MS);
    return () => clearInterval(t);
  }, [loadDevices]);

  // Only screens that are actually on — the same rule the rail uses. A remote offering a
  // screen that is off is offering a button that does nothing.
  //
  // Plus, briefly, any screen this remote just claimed: it is on (somebody is standing in
  // front of it) and its socket is on its way. `now` is already ticking, and the grace
  // window closing is what re-renders such a screen away if it never actually arrives.
  const live = useMemo(() => {
    const on = liveDevices(devices, presence);
    const shown = new Set(on.map((d) => d.id));
    const arriving = devices.filter(
      (d) => !shown.has(d.id) && now - (justClaimed[d.id] ?? 0) < JUST_CLAIMED_MS,
    );
    return [...on, ...arriving];
  }, [devices, presence, justClaimed, now]);
  // Remember a screen was live this mount, so its going away is told rather than silent.
  const [lostState, setLostState] = useState<LostScreenState>(NO_LOST_SCREEN);
  const [lostDismissed, setLostDismissed] = useState(false);
  const online = ready && connected;
  const hadLive = lostState.hadLive;
  useEffect(() => {
    const step = () => setLostState((p) => stepLostScreen(p, live.length, online, Date.now()));
    step();
    if (live.length > 0 || !hadLive) return;
    const t = setInterval(step, 1000);
    return () => clearInterval(t);
  }, [live.length, online, hadLive]);
  useEffect(() => {
    if (live.length > 0) setLostDismissed(false);
  }, [live.length]);
  const lostView = lostScreenView(lostState, live.length, online);
  const showLost = lostView === "card" && !lostDismissed;
  const showChecking = lostView === "checking";
  const reconnecting = hadLive && live.length === 0 && !online;
  // No auto-selection: "nothing selected" is a real state now. Selecting is an ACT —
  // it flashes the chosen TV — so the remote must not perform it on the user's behalf.
  // Choosing what's playing keeps the aim (see point): the next sound tap should hit
  // the same screen without making the user re-select the row.
  const active = live.find((d) => d.id === activeId) ?? null;
  const activeDeviceId = active?.id ?? null;
  // Only the aimed screen can be lit: a lit id that is no longer aimed (switched away, or
  // the screen went off) reads as dark.
  const litDeviceId = litId !== null && litId === activeDeviceId ? litId : null;

  /*
   * The selected TV stays LIT for the whole selection — this effect is the old
   * "Which one?" button made continuous. It pulses identify while the row is selected
   * (each pulse extends the screen's hold window), and its cleanup sends the explicit
   * off — which runs on every way the light ends: the second tap, switching rows, a
   * completed change, and leaving the page.
   *
   * 2026-09-27: keyed on the LIT screen, not the aimed one. Once point() stopped standing
   * the aim down (Casey Low's report), keying this on the aim left the TV under the
   * pulsing "This screen" overlay — re-lit every pulse — on top of the thing just chosen,
   * for as long as the remote stayed open. The light answers "which one?", and after a
   * change that question has been answered.
   */
  useEffect(() => {
    if (!litDeviceId) return;
    const id = litDeviceId;
    sendCommand(id, { kind: "identify" });
    const t = setInterval(() => sendCommand(id, { kind: "identify" }), IDENTIFY_PULSE_MS);
    return () => {
      clearInterval(t);
      sendCommand(id, { kind: "identify", on: false });
    };
  }, [litDeviceId, sendCommand]);

  // Tick while a session runs (there is a clock to advance) and while a just-claimed
  // screen is inside its grace window (there is a window to close).
  const waitingOnClaim = Object.keys(justClaimed).length > 0;
  useEffect(() => {
    if (!session && !waitingOnClaim) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [session, waitingOnClaim]);

  /**
   * A screen was claimed on this remote. Put it in the list at once — the claim answered
   * with the row, so there is nothing to go and fetch — hold it visible through the gap
   * before its socket reports, and AIM THE REMOTE AT IT: you just connected this screen,
   * so it is the one the next press is meant for.
   */
  const onClaimed = useCallback(
    (device: Device) => {
      setLostState(NO_LOST_SCREEN);
      setLostDismissed(false);
      setDevices((prev) =>
        prev.some((d) => d.id === device.id) ? prev : [...prev, { ...device, code: null }],
      );
      setJustClaimed((prev) => ({ ...prev, [device.id]: Date.now() }));
      setNow(Date.now());
      setActiveId(device.id);
      setLitId(device.id);
      void loadDevices();
    },
    [loadDevices],
  );

  /** What a given screen is playing — see resolveShowing, the one resolver. */
  const showingFor = useCallback(
    (d: Device | null): Showing => resolveShowing(d?.showing?.viewId ?? null, views, minted),
    [views, minted],
  );

  const showing = active?.showing?.viewId ?? null;
  const activeShowing = showingFor(active);

  const point = useCallback(
    async (viewId: string, label: string) => {
      if (!active) return;
      try {
        const device = await assignView(active.id, viewId, sendCommand);
        // setShowing answers with a bare Device; keep the code the listing already knew —
        // re-pointing a screen changes nothing about what its tab is wearing.
        setDevices((prev) =>
          prev.map((d) => (d.id === device.id ? { ...device, code: d.code } : d)),
        );
        toast(label, isUserNamedView(viewId));
        // Keep the aim, lower the light. Casey Low (2026-09-24 PM): clearing activeId
        // here dropped the main card and the sound control to "No screen selected" the
        // moment a change landed, so the next sound tap needed the row re-tapped first.
        // The light still goes: the TV is showing what was just picked, not an overlay.
        setLitId(null);
      } catch {
        toast("Couldn't switch");
      }
      setSheet(null);
    },
    [active, sendCommand, toast],
  );

  /** Close every screen. In this model closing a screen IS deleting its device, so the
   *  wall goes back to asking for a code — which is what "stop everything" has to mean
   *  when the person pressing it is not in the room with the TVs. */
  const closeAll = useCallback(async () => {
    await Promise.all(live.map((d) => api.devices.remove(d.id).catch(() => undefined)));
    // Shutting everything down still ends a session, and that session still earned its
    // card — the intent here is "stop everything", not "throw the evening away".
    void api.session
      .stop()
      .then((r) => {
        if (r.session) setSummary({ session: r.session });
      })
      .catch(() => undefined);
    setSession(null);
    await loadDevices();
    // The person closed them: the empty list is theirs, not a lost screen. Without this the
    // remote said "Screen disconnected" five seconds after its own "All screens closed".
    setLostState(NO_LOST_SCREEN);
    toast("All screens closed");
  }, [live, loadDevices, toast]);

  return (
    <div className="flex h-[100dvh] select-none flex-col overflow-hidden bg-[var(--color-ink)] text-zinc-100">
      {/* THE one site bar, same as /views — but CODE-LESS: a controller must never be
          a claimable screen (see SiteChrome). The bespoke header this replaces (the
          brand wordmark and a Library/Views exit; the active-screen line went earlier —
          the rail names the screens) is fully covered: the brand links home, and the
          bar's menu carries Views/Browse/Collections. */}
      <SiteChrome code={false} />

      <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-3 overflow-y-auto p-4">
        {/* With no screens on, this is the whole page — a remote with nothing to drive has
            exactly one useful control, and it is the one that gets it something to drive.
            It stays available afterwards (folded away below) because rooms grow: the
            second and third TV arrive after the first is already playing. */}
        {showLost ? (
          <div
            role="status"
            className="flex items-start justify-between gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm text-amber-100"
          >
            <span>
              Screen disconnected. Open {hostLabel()}/screen and enter the new code to reconnect.
            </span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => setLostDismissed(true)}
              className="shrink-0 text-amber-100/70"
            >
              ✕
            </button>
          </div>
        ) : null}
        {showChecking ? (
          <p role="status" className="text-center text-xs text-zinc-400">
            Checking screen…
          </p>
        ) : null}
        {reconnecting ? <p className="text-center text-xs text-zinc-400">Reconnecting…</p> : null}
        {live.length === 0 && !showChecking ? <ConnectScreen onConnected={onClaimed} prominent /> : null}

        {/* Signed out is FULLY functional here — an anonymous remote claims screens and
            drives them, and that is deliberate. So this sells the one thing it cannot do,
            and sells it honestly: an anonymous identity lives in this browser's storage,
            so it does not travel and it does not survive being cleared.

            Not a modal, not a gate, and dismissible. Whoever is holding this has a TV in
            front of them waiting for something to play; a sign-in wall at that moment
            would be asking them to do paperwork mid-goon. */}
        {!user && !hideSignIn ? (
          <div className="flex items-start gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] leading-relaxed text-zinc-400">
                You&rsquo;re signed out. Your screens and views live in this browser only —
                sign in to keep them on another device.
              </p>
              <button
                type="button"
                onClick={() => setAuth("login")}
                className="mt-1.5 text-xs font-semibold text-[var(--color-accent-soft)]"
              >
                Sign in
              </button>
            </div>
            <button
              type="button"
              onClick={() => setHideSignIn(true)}
              aria-label="Dismiss"
              className="shrink-0 rounded px-1.5 py-0.5 text-sm leading-none text-zinc-500"
            >
              ✕
            </button>
          </div>
        ) : null}

        {/* Every connected screen, and what each is playing.
            This was a single row that only appeared with MORE than one screen, on the
            reasoning that one screen is not a decision. True for *choosing*, wrong for
            *knowing*: with one screen connected, nothing on this page said what it was
            showing. The list answers both — tap to aim the controls, read to see the wall. */}
        {live.length > 0 ? (
          <div className="rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-1">
            {live.map((d) => {
              const s = showingFor(d);
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => {
                    // Toggle. The identify light itself is the selection EFFECT's job
                    // (pulse while lit, off on cleanup) — sending it here too would
                    // double up and still miss the off when point() lowers the light.
                    // Re-tapping an aimed-but-dark row deselects it, same as a lit one.
                    const next = d.id === active?.id ? null : d.id;
                    setActiveId(next);
                    setLitId(next);
                  }}
                  className={cx(
                    "flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left transition-colors",
                    d.id === active?.id ? "bg-[var(--color-accent)]/15" : "",
                  )}
                >
                  {/* The CODE leads, not the name: "Screen 2" identifies nothing, while
                      the code is the same four glyphs the TV's own bubble is wearing —
                      read the glass, find the row. Falls back to the name for a device
                      whose tab announces no code (closed, or lapsed). */}
                  <span className="flex shrink-0 items-center gap-2">
                    <span aria-hidden>{deviceEmoji(d.kind)}</span>
                    <DeviceIdentity device={d} />
                  </span>
                  {/* Icon AND name: the emoji is scannable across rows, the name says
                      which one. The name truncates so a long title cannot squeeze the
                      code — the row's fixed identity — off the left edge. */}
                  <span className="flex min-w-0 shrink items-center gap-1 text-xs text-zinc-400">
                    <span className="whitespace-nowrap">Now showing:</span>
                    <span aria-hidden>{s.icon}</span>
                    <ShowingName showing={s} className="truncate" />
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}

        {/* VIEW — the prominent primary action. Changing what is on the wall is the one
            thing this device exists to do — which is exactly why it is NOT drawn with no
            screens connected: a page whose only real control is "connect one" should not
            also offer a dead button for the thing that comes after. */}
        {live.length > 0 ? (
        <button
          type="button"
          disabled={!active}
          onClick={() => setSheet("view")}
          className="flex flex-1 flex-col items-center justify-center gap-1 rounded-2xl border-2 border-[var(--color-accent)] bg-[var(--color-accent)]/15 py-6 text-center transition-transform active:scale-[0.98] disabled:opacity-40"
        >
          <span className="text-4xl leading-none" aria-hidden>
            ▦
          </span>
          {/* With nothing selected the button says WHY it is disabled: "Nothing" here
              would read as "this screen is idle" when the truth is "no screen is aimed". */}
          <span className="text-xl font-extrabold text-white">
            {active ? <ShowingName showing={activeShowing} /> : "No screen selected"}
          </span>
          <span className="text-xs text-zinc-300/80">
            {active ? "Tap to change what’s playing" : "Tap a screen above to aim the remote"}
          </span>
        </button>
        ) : null}

        {/* SOUND, for the aimed screen. Sound is the screen's — not the view's, not the
            remote's — so this drives the screen's own toggle over the socket, and reads
            the answer back off presence: the screen reports what its browser actually
            allowed, which is not always what was asked (a page nobody has touched may
            refuse to unmute). Unknown reads as muted, because muted is the default every
            screen starts in. Same grammar as VIEW above: present whenever the remote is,
            and says why it is disabled. */}
        {live.length > 0 ? (() => {
          const isMuted = active ? presence[active.id]?.muted !== false : true;
          return (
            <button
              type="button"
              disabled={!active}
              onClick={() => {
                if (!active) return;
                sendCommand(active.id, { kind: "mute", on: !isMuted });
                toast(isMuted ? "Sound on" : "Muted");
              }}
              className={cx(
                "flex items-center justify-center gap-2 rounded-xl border py-3 text-sm font-bold transition-transform active:scale-[0.98] disabled:opacity-40",
                isMuted
                  ? "border-red-500/50 bg-red-500/10 text-red-200"
                  : "border-[var(--color-edge)] bg-[var(--color-panel)] text-zinc-100",
              )}
            >
              <span aria-hidden>{isMuted ? "🔇" : "🔊"}</span>
              {!active
                ? "Select a screen to control its sound"
                : isMuted
                  ? "Muted — tap for sound"
                  : "Sound on — tap to mute"}
            </button>
          );
        })() : null}

        {/* The Skip and "Which one?" buttons stood here and are gone on purpose. Skip
            went entirely — its `next` command left the wire with it. "Which one?" became
            the act of selecting: tapping a row flashes that TV, which is when the answer
            is actually needed. */}

        {/* Goon session — on the face, with the elapsed time always readable. */}
        <div className="rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
              Goon session
            </span>
            {/* Time and clips viewed, together. The wall's own bar has shown both since it
                was built (see GoonSessionBar); the remote showed only the clock, so the
                number that says how much of the session has actually happened was on the
                television across the room and not in your hand. */}
            <span className="flex items-baseline gap-2">
              <span
                className={cx(
                  "font-mono text-lg font-bold tabular-nums",
                  session ? "text-[var(--color-accent-soft)]" : "text-zinc-600",
                )}
              >
                {session ? formatElapsed(now + clockOffset - session.startedAt) : "0:00"}
              </span>
              <span
                className={cx("text-xs tabular-nums", session ? "text-zinc-400" : "text-zinc-600")}
                title="Clips viewed this session"
              >
                👁 {session?.views ?? 0}
              </span>
              {/* Edges beside the views, in the same small grey (owner, 2026-09-19): the
                  two readings of how far the session has gone sit together, and the
                  count is legible without looking down at the button that adds one. The
                  💦 is the edge glyph the clip stats already use. */}
              <span
                className={cx("text-xs tabular-nums", session ? "text-zinc-400" : "text-zinc-600")}
                title="Edges this session"
              >
                💦 {session?.edges ?? 0}
              </span>
            </span>
          </div>
          {session ? (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  void api.session.edge().then((r) => setSession(r.session)).catch(() => undefined);
                  toast("Edge +1");
                }}
                className="rounded-lg bg-[var(--color-accent)]/20 py-3 text-sm font-bold text-[var(--color-accent-soft)] transition-transform active:scale-95"
              >
                {/* Just "Edge" (owner, 2026-09-19): the count lives beside the views in the
                    header now, so the button is the act alone. */}
                Edge
              </button>
              <button
                type="button"
                onClick={() => setConfirmingEnd(true)}
                className="rounded-lg border border-[var(--color-edge)] bg-[var(--color-ink)] py-3 text-sm font-semibold transition-transform active:scale-95"
              >
                ■ End
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                void api.session.start().then((r) => setSession(r.session)).catch(() => undefined);
                setNow(Date.now());
                toast("Session started");
              }}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--color-accent)] py-3 text-sm font-bold text-white transition-transform active:scale-95"
            >
              {/* A RECORD dot here, not the play triangle the screens use, and the
                  difference is the point. On a screen the button starts playback of the
                  thing in front of you. On the remote it starts RECORDING a session — the
                  timer, the edges, the history — while the wall was already playing. Two
                  different acts deserve two different glyphs.

                  The ▶ was literal text, not an icon, so it never took the accent colour
                  or scaled with the button. */}
              <IconClockRecord width={14} height={14} />
              Start Gooning
            </button>
          )}
        </div>

        {/* Adding the NEXT screen, once there is already one. Below the controls rather
            than above them: connecting is a once-per-TV act and steering is continuous, so
            the thing you do constantly keeps the top of the page. */}
        {live.length > 0 ? <ConnectScreen onConnected={onClaimed} /> : null}

        {live.length > 0 ? (
          <button
            type="button"
            onClick={() => void closeAll()}
            className="mt-auto rounded-xl border border-red-500/50 bg-red-500/10 py-3 text-sm font-bold text-red-300 transition-transform active:scale-95"
          >
            ⛔ Close all screens
          </button>
        ) : null}

        {/* THE ONE SURFACE THAT IS RELIABLY A PHONE IN A HAND.
            A link on the wall itself is unreachable — that is a TV across a room — and
            the remote is where a tap actually happens. It also has real dead time: this
            page spends its life waiting to pair, or sitting idle between taps.
            Quiet variant, at the very bottom, so it never competes with the controls.
            `mt-auto` only when nothing above already claimed it (the close-all button
            takes it once screens exist), so it sinks to the bottom in both states. */}
        <div className={cx("flex justify-center pt-6", live.length > 0 ? "" : "mt-auto")}>
          <DiscordLink variant="quiet" />
        </div>
      </div>

      <ConfirmDialog
        open={confirmingEnd}
        title="End this goon session?"
        message="The timer stops and your recap opens."
        confirmLabel="End session"
        onConfirm={() => {
          setConfirmingEnd(false);
          void api.session
            .stop()
            .then((r) => {
              if (r.session) setSummary({ session: r.session });
            })
            .catch(() => undefined);
          setSession(null);
          toast("Session ended");
        }}
        onCancel={() => setConfirmingEnd(false)}
      />

      {summary ? (
        <SessionSummaryModal
          session={summary.session}
          onClose={() => setSummary(null)}
          username={user?.username}
          /* No deviceId: the remote IS the owner, and passing one of the screens' ids
             would ask the server to act as that device instead — same identity here, but
             wrong in principle and wrong the moment a screen changes hands. */
        />
      ) : null}

      {sheet === "screen" ? (
        <BottomSheet title="Which screen" onClose={() => setSheet(null)}>
          <div className="grid gap-2">
            {live.map((d) => (
              <SheetBtn
                key={d.id}
                active={d.id === active?.id}
                onClick={() => {
                  setActiveId(d.id);
                  setSheet(null);
                }}
              >
                <span aria-hidden>{deviceEmoji(d.kind)}</span>
                {/* Device names are user-renamable: masked in replay. */}
                <Masked className="truncate">{d.name}</Masked>
              </SheetBtn>
            ))}
          </div>
        </BottomSheet>
      ) : null}

      {/* The one picker every surface shares — see components/WhatsPlaying. `point`
          is this remote's half of the deal: assign the AIMED device, nudge it, lower its
          light — the aim stays so sound and the next change keep hitting that screen. */}
      {sheet === "view" ? (
        <WhatsPlayingSheet
          showing={showing}
          onClose={() => setSheet(null)}
          onPoint={point}
          onMinted={(id, meta) => setMinted((prev) => ({ ...prev, [id]: meta }))}
        />
      ) : null}

      {auth ? <AuthModal initialMode={auth} onClose={() => setAuth(null)} /> : null}

      {flash ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-24 z-50 flex justify-center">
          <span
            className="rounded-full bg-black/80 px-4 py-2 text-sm font-semibold text-white backdrop-blur"
            {...maskIf(flash.masked)}
          >
            {flash.msg}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Type the code a screen is showing, and take it.
 *
 * This is the half that makes "Remote" a real choice on the front door. Before it, a phone
 * that picked Remote arrived at an empty device list and a sentence telling it to go scan
 * something — and the QR that sentence meant was read by the phone's CAMERA APP, which
 * pairs in a fresh browser tab rather than in the remote you are holding. The remote could
 * drive screens it had somehow been given and could not acquire one itself.
 *
 * `prominent` is the empty state: nothing else is on the page, so this is sized like the
 * page's primary control. Folded small once screens exist, because then it is the rarer
 * act — you connect a TV once and steer it all night.
 *
 * The field itself is `usePairingCode`. It was shared with a desktop card in the screens
 * rail; that card and that rail are gone, and the remote is the one reader of codes — but
 * every rule that is easy to get subtly wrong stays in the hook, not in this chrome.
 */
function ConnectScreen({
  onConnected,
  prominent = false,
}: {
  onConnected: (device: Device) => void;
  prominent?: boolean;
}) {
  const { code, error, heldUntil, busy, onChange, submit } = usePairingCode(onConnected);
  const heldFor = useCountdown(heldUntil);

  return (
    <div
      className={cx(
        "rounded-xl border bg-[var(--color-panel)] p-4",
        prominent
          ? "border-[var(--color-accent)]/60"
          : "border-dashed border-[var(--color-edge)]",
      )}
    >
      <p
        className={cx(
          "font-semibold uppercase tracking-wide",
          prominent ? "text-sm text-zinc-200" : "text-[11px] text-zinc-500",
        )}
      >
        Connect a screen
      </p>
      {/* One address, the same sentence the desktop card prints, and the mirror of what a
          waiting screen prints back. All three come from lib/hostLabel so they cannot
          disagree about what to type. */}
      <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">
        On the TV, open <span className="text-zinc-300">{hostLabel()}/screen</span> &mdash;
        then type the code it shows.
      </p>

      <input
        value={code}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void submit(code);
        }}
        disabled={busy}
        placeholder="CODE"
        aria-label="Code shown on your screen"
        /* `text` rather than a numeric mode: the pairing alphabet has letters in it, and a
           number pad on a phone would put half the valid characters out of reach. */
        inputMode="text"
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        maxLength={PAIRING_CODE_LEN}
        className={cx(
          "mt-3 w-full rounded-lg border border-[var(--color-edge)] bg-[var(--color-ink)] text-center font-mono tracking-[0.35em] text-zinc-100 placeholder:tracking-normal placeholder:text-zinc-600 focus:border-[var(--color-accent)]/60 focus:outline-none disabled:opacity-60",
          // Thumb-sized in the empty state. 16px or larger matters on iOS specifically:
          // below it Safari zooms the viewport on focus and the remote jumps under the hand.
          prominent ? "py-4 text-3xl placeholder:text-lg" : "py-3 text-xl placeholder:text-base",
        )}
      />

      {error ? <p className="mt-2 text-[11px] text-red-300">{error}</p> : null}

      {/* SOMEBODY ELSE HAS IT. Amber rather than the red `error` wears, because nothing
          went wrong here — the code was right and the screen is spoken for. The field
          keeps what they typed, so getting in is one tap once the clock runs out rather
          than four characters again.

          A BUTTON, not an auto-submit: the code may have been retyped while they waited,
          and a form that posts itself while somebody is looking away is a surprise. */}
      {heldUntil !== null ? (
        <div className="mt-2 text-[11px] leading-relaxed text-amber-300">
          {heldFor > 0 ? (
            <>
              Someone else has that screen. You can claim it in{" "}
              <span className="font-semibold tabular-nums">{holdCountdown(heldFor)}</span>.
            </>
          ) : (
            <button
              type="button"
              onClick={() => void submit(code)}
              className="font-semibold underline underline-offset-2"
            >
              That screen is free now — try again
            </button>
          )}
        </div>
      ) : null}

      {busy ? <p className="mt-2 text-[11px] text-zinc-500">Connecting…</p> : null}
    </div>
  );
}

// BottomSheet and SheetBtn moved to components/WhatsPlaying with the picker they framed;
// the "Which screen" sheet above imports them from there.
